import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import moment, { Moment } from 'moment';
import { dateTimeFormats } from '@shared/constants/dateTimeFormats';
import { HdActivePaletteService } from '../../chart/bodygraph/hd-active-palette.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../chart/bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '../../chart/bodygraph/hd-planet-info.service';
import { getHdPlanetDisplayConfig } from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { formatGateLine, msUntil, selectCountdownFormat } from '../transit-countdown.utils';
import { TransitViewTimeService } from '../transit-view-time.service';
import { HdTransitInfoCardData } from './hd-transit-info-card.models';

const I18N_PREFIX = 'humanDesign.chart.transits.card';
/** Gedeelde keys met het Nu & Straks-panel (tooltips, countdowns, rij-labels). */
const PANEL_I18N_PREFIX = 'humanDesign.chart.transits.panel';
/** Gedeelde keys met de shift-popup (titel, range, duren, station). */
const POPUP_I18N_PREFIX = 'humanDesign.chart.transits.popup';

/** Eén cel (vorige/huidige/volgende) van de poort- of lijn-rij. */
interface HdTransitInfoCardCellVm {
  /** Weergave-waarde: poortnummer ("31") of poort.lijn ("31.4"). */
  readonly value: string;
  /** Mini center-icoon boven de waarde (enkel poort-rij), gevuld met de center-kleur. */
  readonly centerIcon: SafeHtml | null;
  /** Center-label als hover-tooltip op het icoon. */
  readonly centerLabel: string | null;
  /** Keynotes onder de waarde (poort: keynote1/2; lijn: lijn-keynote). */
  readonly keynotes: readonly string[];
  /** Volledige keynote-tekst als hover-tooltip op de waarde. */
  readonly tooltip: string | null;
}

/** Weergave-model van de kaart (alle secties met data zijn non-null). */
interface HdTransitInfoCardVm {
  readonly stripeColor: string;
  readonly glyph: string;
  readonly name: string;
  readonly subtitle: string;
  readonly retrogradeLabel: string | null;
  /** Per station binnen het segment: "Draait retrograde · <dag datum tijd>". */
  readonly stationLabels: readonly string[];
  readonly prevLabel: string;
  readonly nowLabel: string;
  readonly nextLabel: string;
  readonly prevGate: HdTransitInfoCardCellVm | null;
  readonly currentGate: HdTransitInfoCardCellVm;
  readonly nextGate: HdTransitInfoCardCellVm | null;
  readonly gateSinceTooltip: string;
  readonly gateShiftAtTooltip: string | null;
  readonly prevLine: HdTransitInfoCardCellVm | null;
  readonly currentLine: HdTransitInfoCardCellVm | null;
  readonly nextLine: HdTransitInfoCardCellVm | null;
  readonly lineSinceTooltip: string;
  readonly lineShiftAtTooltip: string | null;
  /** Tijdsectie onderaan: per rij (poort eerst, dan lijn) countdown + verloop + tijdsspanne. */
  readonly gateTime: HdTransitInfoCardTimeVm | null;
  readonly lineTime: HdTransitInfoCardTimeVm | null;
}

/** Eén rij van de tijdsectie: countdown + progressbar + de tijdsspanne eronder. */
interface HdTransitInfoCardTimeVm {
  readonly text: string;
  /** Klein label rechts ("(poort)" / "(lijn)"). */
  readonly kindLabel: string;
  /** Verstreken deel van het segment in procenten (0-100) voor de progressbar. */
  readonly progressPct: number;
  /** De tijdsspanne van het segment (van–tot), onder de progressbar. */
  readonly range: {
    readonly fromDate: string;
    readonly fromTime: string;
    readonly toDate: string;
    readonly toTime: string;
    readonly connector: string;
  };
}

/**
 * Tick-onafhankelijk deel van één tijdsectie-rij, plus de segmentgrenzen en het
 * vaste label-deel waarmee de live merge per tick de countdown/progress bouwt.
 */
interface HdTransitInfoCardTimeStaticVm {
  readonly kindLabel: string;
  readonly range: HdTransitInfoCardTimeVm['range'];
  readonly startUtc: Moment;
  readonly targetUtc: Moment;
  /** Vast staartje achter de countdown ("… in poort 26" / "… in lijn 5"). */
  readonly labelSuffix: string;
}

/** Tick-onafhankelijke kaart-VM; de tijdsectie draagt hier de statische varianten. */
interface HdTransitInfoCardStaticVm extends Omit<HdTransitInfoCardVm, 'gateTime' | 'lineTime'> {
  readonly gateTime: HdTransitInfoCardTimeStaticVm | null;
  readonly lineTime: HdTransitInfoCardTimeStaticVm | null;
}

/**
 * UTC-Moment → datumdeel in de tijdzone van de kijker. De tijden komen uit
 * geladen segmenten en houden de locale van hun aanmaak (ook na `clone`):
 * de dagnaam volgt de huidige app-locale (wisselt mee met de taal).
 */
function formatLocalDate(time: Moment): string {
  return time.clone().local().locale(moment.locale()).format(dateTimeFormats.dayAsWord);
}

/** UTC-Moment → tijddeel in de tijdzone van de kijker. */
function formatLocalTime(time: Moment): string {
  return time.clone().local().format(dateTimeFormats.timeSpanNoSeconds);
}

/** UTC-Moment → volledige lokale datum + tijd (hover-tooltips op de pijltjes). */
function formatLocalDateTime(time: Moment): string {
  return time.clone().local().locale(moment.locale()).format(dateTimeFormats.dayAsWordWithTime);
}

/** Verstreken deel van [start, end) op `now` in procenten (0-100), voor de progressbars. */
function progressPct(start: Moment, end: Moment, now: Moment): number {
  const total = end.valueOf() - start.valueOf();
  if (total <= 0) return 100;
  const elapsed = now.valueOf() - start.valueOf();
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

/** Keynotes onder een waarde: lege eruit, dubbels (keynote1 === keynote2) ontdubbeld. */
function distinctKeynotes(keynotes: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  return keynotes.filter(keynote => {
    if (keynote.length === 0 || seen.has(keynote)) return false;
    seen.add(keynote);
    return true;
  });
}

/**
 * Transit-info-card (figma-mockup, eigen styleguide-kleuren): per planeet een
 * licht kaartje met afgeronde hoeken — header (glyph + naam + subtitle),
 * dan meteen de poort-rij (grote poortnummers als focus, mini center-icoon
 * erboven, keynote1/keynote2 eronder) en de lijn-rij (lijn-keynote eronder),
 * elk met vorige ← huidige → volgende en prev/now/next-labels. Onderaan de
 * tijdsectie: per rij (lijn/poort) de countdown met een progressbar van het
 * verstreken segmentdeel, met de datumrange als afsluiter. Prev is lichtgrijs,
 * current donker/groot/vet, next + tijdsectie-accenten in de planeet-stripe-kleur.
 *
 * Gedeeld door de shift-popup (boven de planet cheat-sheet card) en de card
 * view van het Nu & Straks-panel; klik-gedrag bepaalt de ouder.
 */
@Component({
  selector: 'hd-transit-info-card',
  templateUrl: './hd-transit-info-card.component.html',
  styleUrl: './hd-transit-info-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdTransitInfoCardComponent {
  readonly data = input.required<HdTransitInfoCardData>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly viewTime = inject(TransitViewTimeService);
  private readonly centerColors = inject(HdActivePaletteService);

  private readonly lang = computed(() => {
    // Meelezen zodat de eerste translate ná een async translation-load opnieuw
    // evalueert (zie HdContentLanguageService.translationsLoadedTick).
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /**
   * Tick-onafhankelijke kaart-VM: alle dure delen (gate-info-lookups,
   * center-SVG's, vertalingen, datum-formatteringen) herrekenen alleen bij
   * data-/taalwijziging — bewust NIET per klok-tick (zie
   * docs/human-design/transits-performance-analyse.md, fix 1).
   */
  private readonly staticVm = computed<HdTransitInfoCardStaticVm>(() => {
    const data = this.data();
    const lang = this.lang();
    const config = getHdPlanetDisplayConfig(data.planetKey);
    const planetInfo = this.planetInfo.planetInfoIn(data.planetKey, lang);
    const t = (key: string, params?: Record<string, unknown>, prefix = I18N_PREFIX): string =>
      this.transloco.translate(`${prefix}.${key}`, params, lang);

    const from = data.fromUtc ?? data.current.sinceUtc;
    const lineEnd = data.toUtc ?? data.nextLine?.atUtc ?? null;
    const gateStart = data.currentGate?.sinceUtc ?? data.current.sinceUtc;
    const rangeVm = (rangeFrom: Moment, rangeTo: Moment): HdTransitInfoCardTimeVm['range'] => ({
      fromDate: formatLocalDate(rangeFrom),
      fromTime: formatLocalTime(rangeFrom),
      toDate: formatLocalDate(rangeTo),
      toTime: formatLocalTime(rangeTo),
      connector: t('fromTo', { from: '', to: '' }, POPUP_I18N_PREFIX).trim(),
    });

    return {
      stripeColor: config.stripeColor,
      glyph: config.astronomiconChar,
      name: planetInfo.name,
      subtitle: planetInfo.subtitle ?? '',
      retrogradeLabel: data.retrograde ? t('retrogradeLabel', undefined, POPUP_I18N_PREFIX) : null,
      stationLabels: (data.stations ?? []).map(
        station =>
          `${t(station.turnsTo === 'direct' ? 'turnsDirectLabel' : 'turnsRetrogradeLabel', undefined, POPUP_I18N_PREFIX)} · ${formatLocalDateTime(station.atUtc)}`,
      ),
      prevLabel: t('prevLabel'),
      nowLabel: t('nowLabel'),
      nextLabel: t('nextLabel'),
      prevGate: data.prevGate != null ? this.gateCell(data.prevGate.gate, lang) : null,
      currentGate: this.gateCell(data.current.gate, lang),
      nextGate: data.nextGate != null ? this.gateCell(data.nextGate.gate, lang) : null,
      gateSinceTooltip: t('sinceLabel', { time: formatLocalDateTime(gateStart) }, PANEL_I18N_PREFIX),
      gateShiftAtTooltip:
        data.nextGate != null
          ? t('shiftAtLabel', { time: formatLocalDateTime(data.nextGate.atUtc) }, PANEL_I18N_PREFIX)
          : null,
      prevLine: data.prevLine != null ? this.lineCell(data.prevLine.gate, data.prevLine.line, lang) : null,
      currentLine: data.current.line != null ? this.lineCell(data.current.gate, data.current.line, lang) : null,
      nextLine: data.nextLine != null ? this.lineCell(data.nextLine.gate, data.nextLine.line, lang) : null,
      lineSinceTooltip: t('sinceLabel', { time: formatLocalDateTime(data.current.sinceUtc) }, PANEL_I18N_PREFIX),
      lineShiftAtTooltip:
        data.nextLine != null
          ? t('shiftAtLabel', { time: formatLocalDateTime(data.nextLine.atUtc) }, PANEL_I18N_PREFIX)
          : null,
      gateTime:
        data.nextGate != null
          ? {
              kindLabel: `(${t('gateShiftLabel', undefined, PANEL_I18N_PREFIX)})`,
              range: rangeVm(gateStart, data.nextGate.atUtc),
              startUtc: gateStart,
              targetUtc: data.nextGate.atUtc,
              labelSuffix: t('timeInGate', { gate: data.current.gate }),
            }
          : null,
      lineTime:
        data.nextLine != null && data.current.line != null && lineEnd != null
          ? {
              kindLabel: `(${t('lineShiftLabel', undefined, PANEL_I18N_PREFIX)})`,
              range: rangeVm(from, lineEnd),
              startUtc: data.current.sinceUtc,
              targetUtc: data.nextLine.atUtc,
              labelSuffix: t('timeInLine', { line: data.current.line }),
            }
          : null,
    };
  });

  /**
   * Publieke VM voor de template: goedkope merge van {@link staticVm} met de
   * twee levende tijd-rijen. Per klok-tick zijn alleen de countdown-teksten en
   * progress-percentages nieuw; alle overige referenties (cellen, SafeHtml's,
   * ranges) blijven identiek zodat de bindings niets herschrijven.
   */
  protected readonly vm = computed<HdTransitInfoCardVm>(() => {
    const staticVm = this.staticVm();
    const now = this.viewTime.viewTime();
    const lang = this.lang();
    const t = (key: string, params?: Record<string, unknown>, prefix = I18N_PREFIX): string =>
      this.transloco.translate(`${prefix}.${key}`, params, lang);
    const liveTime = (time: HdTransitInfoCardTimeStaticVm | null): HdTransitInfoCardTimeVm | null =>
      time == null
        ? null
        : {
            text: `${this.countdownText(time.targetUtc, now, t)} ${time.labelSuffix}`,
            kindLabel: time.kindLabel,
            progressPct: progressPct(time.startUtc, time.targetUtc, now),
            range: time.range,
          };

    return {
      ...staticVm,
      gateTime: liveTime(staticVm.gateTime),
      lineTime: liveTime(staticVm.lineTime),
    };
  });

  /** Poort-cel: mini center-icoon + keynote1/keynote2 (ontdubbeld). */
  private gateCell(gate: number, lang: string): HdTransitInfoCardCellVm {
    const info = this.gateInfo.gateInfoIn(gate, lang);
    const keynotes = distinctKeynotes([info.translations.keynote1, info.translations.keynote2]);
    return {
      value: `${gate}`,
      centerIcon: this.centerColors.iconHtml(info.center, 'display:block;width:auto;height:100%;overflow:visible'),
      centerLabel: this.gateInfo.centerLabelIn(info.center, lang),
      keynotes,
      tooltip: keynotes.length > 0 ? keynotes.join(' · ') : null,
    };
  }

  /** Lijn-cel: "31.4" met de lijn-keynote van die poort. */
  private lineCell(gate: number, line: number, lang: string): HdTransitInfoCardCellVm {
    const info = this.gateInfo.gateInfoIn(gate, lang);
    const keynote = info.translations.lines[line - 1]?.keynote ?? '';
    return {
      value: formatGateLine(gate, line),
      centerIcon: null,
      centerLabel: null,
      keynotes: keynote.length > 0 ? [keynote] : [],
      tooltip: keynote.length > 0 ? keynote : null,
    };
  }

  private countdownText(
    target: Moment,
    now: Moment,
    t: (key: string, params?: Record<string, unknown>, prefix?: string) => string,
  ): string {
    const format = selectCountdownFormat(msUntil(target, now));
    return t(format.key, format.params, PANEL_I18N_PREFIX);
  }
}
